import { memo, useCallback, useMemo, useState } from 'react'
import { When } from 'react-if'
import { useMediaQuery } from 'react-responsive'
import { CircleAlertIcon } from '@/atoms/Icons/CircleAlertIcon'
import { CopyIcon } from '@/atoms/Icons/CopyIcon'
import { useToast } from '@/molecules/Toast'
import { createOneToManyShareLink } from '@/services/ContentShareService'
import { useGuildMember } from '@/services/GuildUserService'
import { useUser } from '@/services/UserService'
import { useLocale } from '@/utils/LocaleUtil'
import { useSafeTrack } from '@/utils/analytics'
import { createDebouncedFunction } from '@/utils/function-utils'
import { logger } from '@/utils/logging'
import { getBasePath } from '@/utils/sitemap/base'
import { useTranslate } from '@/utils/translate/translate-client'
import { getUserDisplayName } from '@/utils/users'
import { OneToManyModal } from './OneToManyModal'
import { OneToManySheet } from './OneToManySheet'

interface OneToManyContentSharingModalProps {
  projectSlug: string
  projectName?: string
  moviePosterPath?: string | null
  mobileMoviePosterPath?: string | null
  episodeId?: string
  showShareModal: boolean
  setShowShareModal: (showShareModal: boolean) => void
  isGuildShareAvailable?: boolean
}

export const OneToManyContentSharingModal: React.FC<OneToManyContentSharingModalProps> = memo(
  ({
    projectSlug,
    projectName,
    moviePosterPath,
    mobileMoviePosterPath,
    episodeId,
    showShareModal,
    setShowShareModal,
    isGuildShareAvailable = false,
  }) => {
    const { t } = useTranslate('watch')
    const track = useSafeTrack()
    const toast = useToast()
    const { locale } = useLocale()
    const { user } = useUser()
    const { isGuildMember } = useGuildMember()

    const [shareUrl, setShareUrl] = useState('')
    const [linkError, setLinkError] = useState<string | null>(null)
    const [isCreateLoading, setIsCreateLoading] = useState(false)

    const displayName = getUserDisplayName(user)

    const shareLinkCopiedText = t('shareLinkCopied', 'Share Link Copied.')
    const shareLinkErrorText = t('shareLinkError', 'There was an error copying the link. Please try again.')
    const unknownErrorText = t('unknownShareError', 'An error occurred! Please try again later.')
    const mustBeGuildMemberErrorText = t('mustBeGuildMemberError', 'You must be a guild member to create a share link.')

    const baseUrl = getBasePath(locale)
    const canCreateShareLink = isGuildShareAvailable && Boolean(episodeId)

    const handleLinkCopied = useCallback(() => {
      navigator.clipboard
        .writeText(shareUrl)
        .then(() => {
          toast?.showToast(shareLinkCopiedText, { icon: <CopyIcon size={24} color="core-gray-200" /> })
        })
        .catch(() => {
          toast?.showToast(shareLinkErrorText, { icon: <CircleAlertIcon size={24} color="core-gray-200" /> })
        })
    }, [shareLinkCopiedText, shareLinkErrorText, shareUrl, toast])

    const createAndCopyLink = useMemo(() => {
      return createDebouncedFunction(
        () => {
          if (!episodeId) return

          if (!isGuildMember) {
            setLinkError(mustBeGuildMemberErrorText)
            return
          }

          setIsCreateLoading(true)
          setLinkError(null)

          createOneToManyShareLink({ contentId: episodeId, baseUrl, episodeId })
            .then((generatedUrl) => {
              if (!generatedUrl) {
                setLinkError(unknownErrorText)
                return
              }

              track('Guild Content Share Link Created', {
                sharer_id: user?.id,
                slug: projectSlug,
                content_uuid: episodeId,
                type: 'one-to-many',
              })

              setShareUrl(generatedUrl)
              navigator.clipboard
                .writeText(generatedUrl)
                .then(() => {
                  toast?.showToast(shareLinkCopiedText, { icon: <CopyIcon size={24} color="core-gray-200" /> })
                })
                .catch((err) => {
                  logger().warn(
                    'Failed to programmatically write the share link to the clipboard',
                    { generatedUrl },
                    err,
                  )
                })
            })
            .catch((error) => {
              setLinkError(unknownErrorText)
              logger().error('Error creating share link', error)
            })
            .finally(() => {
              setIsCreateLoading(false)
            })
        },
        { wait: 5000, maxWait: 10000, leading: true, trailing: false },
      )
    }, [
      episodeId,
      baseUrl,
      toast,
      track,
      isGuildMember,
      mustBeGuildMemberErrorText,
      user?.id,
      projectSlug,
      unknownErrorText,
      shareLinkCopiedText,
    ])

    const handleModalClose = () => {
      setShowShareModal(false)
    }

    const isMobile = useMediaQuery({ query: '(max-width: 767px)' })

    return (
      <When condition={canCreateShareLink}>
        <When condition={isMobile}>
          <OneToManySheet
            showModal={showShareModal}
            handleModalClose={handleModalClose}
            createCopyLink={createAndCopyLink}
            moviePosterPath={mobileMoviePosterPath}
            projectName={projectName}
            displayName={displayName}
            shareUrl={shareUrl}
            handleLinkCopied={handleLinkCopied}
            isCreateLoading={isCreateLoading}
            linkError={linkError}
          />
        </When>
        <When condition={!isMobile}>
          <OneToManyModal
            showModal={showShareModal}
            handleModalClose={handleModalClose}
            createCopyLink={createAndCopyLink}
            moviePosterPath={moviePosterPath}
            projectName={projectName}
            displayName={displayName}
            shareUrl={shareUrl}
            handleLinkCopied={handleLinkCopied}
            isCreateLoading={isCreateLoading}
            linkError={linkError}
          />
        </When>
      </When>
    )
  },
)

OneToManyContentSharingModal.displayName = 'React.memo(OneToManyContentSharingModal)'
